import React, {useEffect, useState, useCallback} from "react";
import SectionHeading from "./SectionHeading";
import {useNavigate} from "react-router-dom";
import {AiOutlineLoading3Quarters} from "react-icons/ai";
import {FaRegFrown} from "react-icons/fa";
import {
  flattenNeuronRecommendationItems,
  getNeuronRecommendations,
  NON_COMMENT_SCOPE,
} from "../utils/getNeuronRecommendations";
import {RECOMMENDATION_CONTEXTS} from "../config/recommendationContexts";
import {useDebugMode} from "../hooks/useDebugMode";
import RecommendationWhyBadge from "./RecommendationWhyBadge";

const SimilarContentRail = ({articleId, userEmail}) => {
  const [similarStories, setSimilarStories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const debugMode = useDebugMode();

  const fetchSimilarContent = useCallback(async () => {
    try {
      const response = await getNeuronRecommendations({
        userId: userEmail,
        contextId: RECOMMENDATION_CONTEXTS.youMayAlsoLike,
        scope: NON_COMMENT_SCOPE,
      });

      if (response) {
        const similarData = flattenNeuronRecommendationItems(response)
          .filter((item) => item?.metadata?.type !== "comment")
          .map((item) => {
            const meta = item.metadata;
            if (!meta) return null;
            return {
              id: item.id,
              title: meta.originalTitle || item.name || "Untitled",
              summary: meta.summary || item.description || "",
              image: meta.image || "",
              source: meta.source || "",
              aiCategorization: meta.aiCategorization || "",
              timeToRead: meta.timeToRead || "",
              matchPercentage: (Number(item.score ?? 0) * 100).toFixed(2),
              recommendationSource: item.recommendationSource || "",
              recommendationExplanation:
                item.recommendationExplanation || null,
            };
          })
          .filter(Boolean);

        setSimilarStories(similarData);
      }
    } catch (error) {
      console.error("Error fetching similar content:", error);
    } finally {
      setIsLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (!articleId || !userEmail) {
      setSimilarStories([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    fetchSimilarContent();
  }, [articleId, fetchSimilarContent, userEmail]);

  const handleStoryClick = (story) => {
    if (!story?.id) return;
    try {
      sessionStorage.setItem(`neuron:article:${story.id}`, JSON.stringify({
        uuid: story.id,
        originalTitle: story.title,
        aiSummary: story.summary,
        image: story.image,
        source: story.source,
        aiCategorization: story.aiCategorization,
        timeToRead: story.timeToRead,
      }));
    } catch { /* ignore */ }
    navigate(`/article/${story.id}`);
  };

  return (
    <section className="mt-12">
      <SectionHeading
        kicker="Related"
        title="More on this"
        standfirst="Related pieces, re-ranked against your profile rather than fixed lists."
        debugNote="ctx 2324 · related items · filter type!=comment"
        debugMode={debugMode}
      />

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <AiOutlineLoading3Quarters className="mb-4 animate-spin text-3xl text-ink-muted" />
          <p className="text-body text-ink-tertiary">Finding related stories...</p>
        </div>
      ) : similarStories.length > 0 ? (
        <div className="overflow-x-auto scrollbar-hide snap-x snap-mandatory snap-start scroll-pl-2 flex space-x-4 px-4">
          {similarStories.map((story, index) => (
            <div
              key={story.id}
              className={`group snap-start transform transition-transform duration-300 hover:cursor-pointer shrink-0 h-96 w-5/6 sm:h-96 bg-stone-900 rounded-sm relative overflow-hidden ${
                index === 0 ? "ml-20" : ""
              }`}
              onClick={() => handleStoryClick(story)}
            >
              <img
                src={story.image}
                alt={story.title}
                className="w-full h-full object-cover rounded-sm"
              />
              <RecommendationWhyBadge
                explanation={story.recommendationExplanation}
                source={story.recommendationSource}
                className="absolute top-2 right-2 z-20"
                align="right"
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent text-white rounded-sm transition-opacity ease-in-out duration-300">
                <h3 className="text-2xl font-bold mb-2 sm:text-lg line-clamp-2">
                  {story.title}
                </h3>
                <p className="text-gray-200 text-sm sm:text-sm line-clamp-3">
                  {story.summary}
                </p>
                <p className="text-gray-400 text-xs mt-2 sm:text-xs">
                  Match: {story.matchPercentage || "N/A"}%
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center text-center justify-center mx-auto py-20">
          <FaRegFrown className="text-5xl text-gray-500 mb-4" />
          <p className="text-lg text-gray-500">
            No similar stories available at the moment.
          </p>
        </div>
      )}
    </section>
  );
};

export default SimilarContentRail;
